import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  RiArrowLeftLine, 
  RiFileTextLine, 
  RiUserStarLine,
  RiCalendarLine,
  RiBookOpenLine,
  RiDownloadLine,
  RiEyeLine,
  RiStarFill,
  RiFileCopyLine,
  RiLinksLine,
  RiSearchLine,
  RiFilterLine,
  RiCloseLine,
  RiMailLine,
  RiGlobalLine,
  RiDoubleQuotesL,
  RiDoubleQuotesR,
  RiInformationLine,
  RiArrowUpLine,
  RiMicroscopeLine,
  RiAddLine,
  RiRefreshLine
} from 'react-icons/ri';
import axios from 'axios';
import { API_BASE } from '../config';
import toast from 'react-hot-toast';
import { useSummit } from "../pages/summitdetails";

/* ─── THEME COLORS ───────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradPurple: "linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)",
  gradGreen: "linear-gradient(135deg, #10B981 0%, #34D399 100%)",
  success: "#10B981",
  warning: "#D4A017",
  danger: "#218577",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  
  ::-webkit-scrollbar{width:6px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* Responsive Styles */
  @media(max-width:768px){
    .t-hero-section{padding:clamp(32px,5vw,48px) clamp(16px,3vw,32px) clamp(24px,4vw,40px)!important}
    .t-hero-title{font-size:clamp(1.6rem,4vw,2.4rem)!important}
    .t-stats-wrap{gap:8px!important}
    .t-stat-item{padding:12px 16px!important;min-width:80px!important}
    .t-stat-number{font-size:22px!important}
    .t-publication-card{flex-direction:column!important;min-height:auto!important}
    .t-publication-cover{flex:0 0 auto!important;width:100%!important;min-height:140px!important;border-right:none!important;border-bottom:1px solid ${T.borderLight}!important}
    .t-publication-content{padding:16px!important}
    .t-publication-actions{flex-wrap:wrap!important;justify-content:center!important}
    .t-search-bar{flex-direction:column!important;align-items:stretch!important}
    .t-search-input{min-width:auto!important}
    .t-filter-select{width:100%!important}
    .t-venue-card{flex-direction:column!important;padding:16px!important}
    .t-venue-cover{flex:0 0 auto!important;width:100%!important;min-height:60px!important}
    .t-modal-content{padding:16px!important}
    .t-modal-header{padding:16px 20px!important;flex-wrap:wrap!important}
    .t-modal-title{font-size:20px!important}
  }

  @media(max-width:480px){
    .t-hero-title{font-size:1.4rem!important}
    .t-stat-item{padding:10px 12px!important;min-width:70px!important}
    .t-stat-number{font-size:18px!important}
    .t-stat-label{font-size:10px!important}
    .t-publication-cover{min-height:120px!important}
    .t-publication-content{padding:12px!important}
    .t-publication-title{font-size:16px!important}
    .t-publication-desc{font-size:12px!important}
    .t-action-buttons{flex-direction:column!important;width:100%!important}
    .t-action-buttons button{width:100%!important;justify-content:center!important}
    .t-venue-card{padding:12px!important}
    .t-venue-title{font-size:18px!important}
    .t-modal-header{padding:14px 16px!important}
    .t-modal-title{font-size:18px!important}
    .t-modal-body{padding:16px!important}
    .t-modal-close{width:32px!important;height:32px!important}
    .t-back-to-top{width:36px!important;height:36px!important;bottom:16px!important;right:16px!important}
  }

  @media(max-width:360px){
    .t-hero-title{font-size:1.2rem!important}
    .t-stat-item{padding:8px 10px!important;min-width:60px!important}
    .t-stat-number{font-size:16px!important}
    .t-publication-title{font-size:15px!important}
    .t-publication-cover{min-height:100px!important}
    .t-modal-title{font-size:16px!important}
    .t-modal-body{padding:12px!important}
  }
`;

export default function SummitPublications() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { eventId: paramEventId } = useParams();
  const summitContext = useSummit();
  
  const eventId = summitContext?.eventId || searchParams.get("eventId") || paramEventId;
  
  const [publications, setPublications] = useState([]);
  const [filteredPublications, setFilteredPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState(['all']);
  const [selectedPublication, setSelectedPublication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showBtt, setShowBtt] = useState(false);
  const [eventTitle, setEventTitle] = useState('Meetings21');

  useEffect(() => {
    if (eventId) {
      fetchPublications();
      fetchEventDetails();
    } else {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    const fn = () => setShowBtt(window.scrollY > 500);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showModal]);

  const fetchEventDetails = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/events/${eventId}`);
      if (response.data && response.data.success) {
        setEventTitle(response.data.event?.title || response.data.event?.name || 'Meetings21');
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
    }
  };

  const fetchPublications = async () => {
    try {
      setLoading(true);
      const timestamp = Date.now();
      const url = `${API_BASE}/api/summit-publications/event/${eventId}?_=${timestamp}`;
      const response = await axios.get(url);
      
      let publicationsData = [];
      if (response.data && response.data.success) {
        publicationsData = response.data.data || [];
      } else if (Array.isArray(response.data)) {
        publicationsData = response.data;
      } else if (response.data && response.data.publications) {
        publicationsData = response.data.publications;
      }
      
      setPublications(publicationsData);
      setFilteredPublications(publicationsData);
      
      if (publicationsData && publicationsData.length > 0) {
        const cats = ['all', ...new Set(publicationsData.map(p => p.category).filter(Boolean))];
        setCategories(cats);
      }
    } catch (error) {
      console.error('Error fetching publications:', error);
      toast.error('Failed to load publications');
      setPublications([]);
      setFilteredPublications([]);
    } finally {
      setLoading(false);
    }
  };

  const seedDefaultPublications = async () => {
    if (!eventId) {
      toast.error('No event ID available');
      return;
    }
    setSeeding(true);
    try {
      const response = await axios.post(
        `${API_BASE}/api/summit-publications/seed/event/${eventId}`,
        {},
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data.success) {
        toast.success(response.data.message || 'Default publications seeded successfully!');
        await fetchPublications();
      } else {
        toast.error(response.data.message || 'Failed to seed publications');
      }
    } catch (error) {
      console.error('Error seeding publications:', error);
      toast.error(error.response?.data?.message || 'Failed to seed default publications');
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => {
    if (!publications || publications.length === 0) {
      setFilteredPublications([]);
      return;
    }
    
    let filtered = [...publications];
    
    if (searchTerm) {
      filtered = filtered.filter(pub => 
        pub.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pub.authors?.some(author => author.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        pub.keywords?.some(keyword => keyword?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        pub.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(pub => pub.category === selectedCategory);
    }
    
    setFilteredPublications(filtered);
  }, [searchTerm, selectedCategory, publications]);

  const handleDownload = async (pubId, e) => {
    e.stopPropagation();
    try {
      const response = await axios.post(`${API_BASE}/api/summit-publications/${pubId}/download`);
      if (response.data.success) {
        toast.success('Download recorded!');
      }
    } catch (error) {
      console.error('Error recording download:', error);
    }
    
    const pub = publications.find(p => p._id === pubId);
    if (pub && pub.pdfUrl) {
      window.open(pub.pdfUrl, '_blank');
    }
  };

  const openDetailsModal = (pub) => {
    setSelectedPublication(pub);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPublication(null);
  };

  const getCategoryColor = (category) => {
    const colors = {
      research: T.primary,
      review: "#8B5CF6",
      "case-study": T.success,
      "clinical-trial": T.warning,
      editorial: T.danger,
      venue: T.accentGold,
      other: T.textMuted
    };
    return colors[category] || T.textMuted;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      research: "Research Article",
      review: "Review Article",
      "case-study": "Case Study",
      "clinical-trial": "Clinical Trial",
      editorial: "Editorial",
      venue: "Venue Info",
      other: "Other"
    };
    return labels[category] || category || "Publication";
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: T.offWhite, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{css}</style>
        <div style={{ width: 'clamp(36px,4vw,48px)', height: 'clamp(36px,4vw,48px)', border: `3px solid ${T.border}`, borderTop: `3px solid ${T.primary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  return (
    <>
      <style>{css}</style>
      
      <div style={{ minHeight: '100vh', background: T.offWhite }}>

        {/* Hero Section */}
        <section className="t-hero-section" style={{ 
          padding: 'clamp(32px,6vw,80px) clamp(16px,4vw,64px) clamp(32px,5vw,64px)', 
          background: T.gradSoft, 
          borderBottom: `1px solid ${T.border}`, 
          position: 'relative', 
          overflow: 'hidden' 
        }}>
          <div style={{ maxWidth: 1320, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 'clamp(14px,2vw,20px)', flexWrap: 'wrap' }}>
              <span style={{ 
                padding: 'clamp(4px,0.6vw,5px) clamp(10px,1.2vw,14px)', 
                borderRadius: 20, 
                background: T.softTeal, 
                border: `1px solid ${T.border}`, 
                color: T.primary, 
                fontSize: 'clamp(10px,0.8vw,11px)', 
                fontWeight: 700, 
                textTransform: 'uppercase', 
                letterSpacing: '0.08em' 
              }}>
                <RiMicroscopeLine size="clamp(11px,0.9vw,13px)" style={{ display: 'inline', marginRight: 5 }} />
                Publications
              </span>
            </div>
            <h1 className="t-hero-title" style={{ 
              fontFamily: "'Playfair Display', serif", 
              fontSize: 'clamp(1.6rem,4.5vw,3.4rem)', 
              fontWeight: 900, 
              color: T.navy, 
              letterSpacing: '-0.03em', 
              lineHeight: 1.06, 
              marginBottom: 'clamp(10px,1.5vw,14px)' 
            }}>
              {eventTitle} <span style={{ color: T.accentGold, fontStyle: 'normal' }}>Publications</span>
            </h1>
            <p style={{ 
              fontSize: 'clamp(0.85rem,1.3vw,1rem)', 
              color: T.textMid, 
              lineHeight: 1.7, 
              maxWidth: 560 
            }}>
              Explore research papers, reviews, and proceedings from our academic events
            </p>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 'clamp(16px,2.5vw,24px)' }}>
              <div style={{ flex: 1, height: 2, maxWidth: 60, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 'clamp(6px,0.7vw,8px)', height: 'clamp(6px,0.7vw,8px)', background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, maxWidth: 60, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: 'clamp(16px,3vw,24px) clamp(12px,4vw,40px) 0' }}>
          <div className="t-stats-wrap" style={{ display: 'flex', gap: 'clamp(8px,1.5vw,16px)', flexWrap: 'wrap', marginBottom: 'clamp(16px,2.5vw,24px)' }}>
            <div className="t-stat-item" style={{ 
              background: T.white, 
              borderRadius: 10, 
              padding: 'clamp(12px,2vw,16px) clamp(16px,2.5vw,24px)', 
              border: `1px solid ${T.border}`, 
              flex: '1 1 auto', 
              minWidth: 'clamp(80px,15vw,120px)', 
              boxShadow: T.shadow 
            }}>
              <div className="t-stat-number" style={{ fontSize: 'clamp(22px,3vw,28px)', fontWeight: 800, color: T.accentGold }}>{publications.length}</div>
              <div className="t-stat-label" style={{ fontSize: 'clamp(10px,0.8vw,12px)', color: T.textMuted }}>Total Publications</div>
            </div>
            <div className="t-stat-item" style={{ 
              background: T.white, 
              borderRadius: 10, 
              padding: 'clamp(12px,2vw,16px) clamp(16px,2.5vw,24px)', 
              border: `1px solid ${T.border}`, 
              flex: '1 1 auto', 
              minWidth: 'clamp(80px,15vw,120px)', 
              boxShadow: T.shadow 
            }}>
              <div className="t-stat-number" style={{ fontSize: 'clamp(22px,3vw,28px)', fontWeight: 800, color: T.primary }}>{categories.length - 1}</div>
              <div className="t-stat-label" style={{ fontSize: 'clamp(10px,0.8vw,12px)', color: T.textMuted }}>Categories</div>
            </div>
            <div className="t-stat-item" style={{ 
              background: T.white, 
              borderRadius: 10, 
              padding: 'clamp(12px,2vw,16px) clamp(16px,2.5vw,24px)', 
              border: `1px solid ${T.border}`, 
              flex: '1 1 auto', 
              minWidth: 'clamp(80px,15vw,120px)', 
              boxShadow: T.shadow 
            }}>
              <div className="t-stat-number" style={{ fontSize: 'clamp(22px,3vw,28px)', fontWeight: 800, color: T.accentTealLight }}>
                {publications.reduce((sum, pub) => sum + (pub.downloads || 0), 0)}
              </div>
              <div className="t-stat-label" style={{ fontSize: 'clamp(10px,0.8vw,12px)', color: T.textMuted }}>Total Downloads</div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 clamp(12px,4vw,40px) ' + 'clamp(40px,6vw,80px)' }}>
          
          {/* Action Buttons */}
          <div style={{ 
            marginBottom: 'clamp(24px,3.5vw,32px)', 
            display: 'flex', 
            gap: 'clamp(8px,1vw,12px)', 
            flexWrap: 'wrap', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
          }}>
            <Link 
              to={`/agenda/${eventId}`} 
              style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 'clamp(6px,0.8vw,8px)', 
                color: T.primary, 
                textDecoration: 'none',
                fontWeight: 500,
                padding: '6px 0',
                fontSize: 'clamp(13px,1.1vw,14px)',
                transition: 'gap 0.3s'
              }}
              onMouseEnter={e => e.currentTarget.style.gap = '12px'}
              onMouseLeave={e => e.currentTarget.style.gap = '8px'}
            >
              <RiArrowLeftLine size="clamp(16px,1.5vw,20px)" /> Back to Summit
            </Link>
            
            {publications.length === 0 && (
              <button
                onClick={seedDefaultPublications}
                disabled={seeding}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'clamp(6px,0.8vw,8px)',
                  padding: 'clamp(8px,1vw,10px) clamp(16px,2vw,24px)',
                  borderRadius: 40,
                  background: T.gradGold,
                  color: T.navyDeep,
                  border: 'none',
                  cursor: seeding ? 'not-allowed' : 'pointer',
                  fontSize: 'clamp(13px,1.1vw,14px)',
                  fontWeight: 600,
                  opacity: seeding ? 0.7 : 1,
                  fontFamily: "'Inter', sans-serif"
                }}
              >
                {seeding ? <div style={{ width: 'clamp(14px,1.2vw,16px)', height: 'clamp(14px,1.2vw,16px)', border: `2px solid ${T.navyDeep}`, borderTop: `2px solid transparent`, borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} /> : <RiAddLine size="clamp(14px,1.2vw,16px)" />}
                {seeding ? 'Seeding...' : 'Load Sample Publications'}
              </button>
            )}
            
            {publications.length > 0 && (
              <button
                onClick={fetchPublications}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'clamp(6px,0.8vw,8px)',
                  padding: 'clamp(8px,1vw,10px) clamp(16px,2vw,24px)',
                  borderRadius: 40,
                  background: T.white,
                  color: T.primary,
                  border: `1px solid ${T.border}`,
                  cursor: 'pointer',
                  fontSize: 'clamp(13px,1.1vw,14px)',
                  fontWeight: 600,
                  fontFamily: "'Inter', sans-serif"
                }}
              >
                <RiRefreshLine size="clamp(14px,1.2vw,16px)" /> Refresh
              </button>
            )}
          </div>

          {/* Search and Filter Bar */}
          {publications && publications.length > 0 && (
            <div className="t-search-bar" style={{ 
              background: T.white, 
              borderRadius: 'clamp(14px,1.8vw,16px)', 
              padding: 'clamp(14px,2vw,20px)', 
              marginBottom: 'clamp(24px,3vw,32px)',
              boxShadow: T.shadow,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'clamp(10px,1.5vw,16px)',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div className="t-search-input" style={{ 
                flex: 1, 
                minWidth: 'clamp(200px,30vw,250px)',
                display: 'flex', 
                alignItems: 'center', 
                gap: 'clamp(8px,1vw,12px)',
                padding: 'clamp(6px,0.8vw,8px) clamp(12px,1.5vw,16px)',
                background: T.offWhite,
                borderRadius: 40,
                border: `1px solid ${T.border}`
              }}>
                <RiSearchLine size="clamp(16px,1.3vw,18px)" style={{ color: T.textMuted }} />
                <input
                  type="text"
                  placeholder="Search publications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    flex: 1,
                    border: 'none',
                    background: 'transparent',
                    outline: 'none',
                    fontSize: 'clamp(13px,1.1vw,14px)',
                    fontFamily: "'Inter', sans-serif",
                    minWidth: '60px'
                  }}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: T.textMuted,
                      padding: '4px'
                    }}
                  >
                    <RiCloseLine size="clamp(14px,1.2vw,16px)" />
                  </button>
                )}
              </div>
              
              <div className="t-filter-select" style={{ display: 'flex', alignItems: 'center', gap: 'clamp(8px,1vw,12px)', flexWrap: 'wrap' }}>
                <RiFilterLine size="clamp(14px,1.2vw,16px)" style={{ color: T.textMuted }} />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  style={{
                    padding: 'clamp(6px,0.8vw,8px) clamp(28px,2.5vw,32px) clamp(6px,0.8vw,8px) clamp(12px,1.5vw,16px)',
                    borderRadius: 40,
                    border: `1px solid ${T.border}`,
                    background: T.offWhite,
                    fontSize: 'clamp(13px,1.1vw,14px)',
                    cursor: 'pointer',
                    outline: 'none',
                    fontFamily: "'Inter', sans-serif",
                    minWidth: 'clamp(120px,18vw,160px)'
                  }}
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {cat === 'all' ? 'All Categories' : getCategoryLabel(cat)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Publications Grid */}
          {!publications || publications.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: 'clamp(40px,6vw,80px) 20px', 
              background: T.white, 
              borderRadius: 'clamp(16px,2.5vw,24px)',
              boxShadow: T.shadow,
              border: `1px solid ${T.border}`
            }}>
              <RiFileTextLine size="clamp(48px,6vw,64px)" style={{ color: T.textMuted, marginBottom: 16 }} />
              <h3 style={{ fontSize: 'clamp(18px,2vw,20px)', fontWeight: 700, color: T.text, marginBottom: 8 }}>No publications found</h3>
              <p style={{ color: T.textMuted, marginBottom: 20, fontSize: 'clamp(13px,1.1vw,14px)' }}>Click "Load Sample Publications" to add demo content</p>
              <button
                onClick={seedDefaultPublications}
                disabled={seeding}
                style={{
                  padding: 'clamp(10px,1.2vw,12px) clamp(20px,2.5vw,28px)',
                  borderRadius: 40,
                  background: T.gradGold,
                  color: T.navyDeep,
                  border: 'none',
                  cursor: seeding ? 'not-allowed' : 'pointer',
                  fontSize: 'clamp(13px,1.1vw,14px)',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'clamp(6px,0.8vw,8px)',
                  opacity: seeding ? 0.7 : 1,
                  fontFamily: "'Inter', sans-serif"
                }}
              >
                {seeding ? <div style={{ width: 'clamp(14px,1.2vw,16px)', height: 'clamp(14px,1.2vw,16px)', border: `2px solid ${T.navyDeep}`, borderTop: `2px solid transparent`, borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} /> : <RiAddLine size="clamp(16px,1.4vw,18px)" />}
                {seeding ? 'Loading...' : 'Load Sample Publications'}
              </button>
            </div>
          ) : filteredPublications.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: 'clamp(32px,5vw,60px) 20px', 
              background: T.white, 
              borderRadius: 'clamp(16px,2.5vw,24px)',
              boxShadow: T.shadow,
              border: `1px solid ${T.border}`
            }}>
              <RiSearchLine size="clamp(36px,4.5vw,48px)" style={{ color: T.textMuted, marginBottom: 16 }} />
              <h3 style={{ fontSize: 'clamp(17px,1.8vw,18px)', fontWeight: 700, color: T.text, marginBottom: 8 }}>No matching publications</h3>
              <p style={{ color: T.textMuted, fontSize: 'clamp(13px,1.1vw,14px)' }}>Try adjusting your search or filter criteria</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                }}
                style={{
                  marginTop: 14,
                  padding: 'clamp(6px,0.8vw,8px) clamp(18px,2vw,24px)',
                  borderRadius: 40,
                  background: T.gradGold,
                  color: T.navyDeep,
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600,
                  fontSize: 'clamp(13px,1.1vw,14px)'
                }}
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(24px,3.5vw,32px)' }}>
              {filteredPublications.map((pub, index) => {
                if (pub.category === 'venue') {
                  return (
                    <motion.div
                      key={pub._id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="t-venue-card"
                      style={{
                        background: T.white,
                        borderRadius: 'clamp(16px,2vw,20px)',
                        padding: 'clamp(16px,2.5vw,24px) clamp(20px,3vw,32px)',
                        boxShadow: T.shadowMd,
                        border: `2px solid ${T.accentGold}`,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 'clamp(16px,2vw,24px)',
                      }}
                    >
                      <div className="t-venue-cover" style={{ 
                        flex: '0 0 clamp(60px,8vw,80px)', 
                        minHeight: 'clamp(60px,8vw,80px)',
                      }}>
                        {pub.coverImage ? (
                          <img
                            src={pub.coverImage}
                            alt={pub.title || 'Venue'}
                            style={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: 'clamp(60px,8vw,80px)',
                              objectFit: 'contain',
                              borderRadius: 8,
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div style={{ 
                            width: '100%',
                            height: '100%',
                            minHeight: 'clamp(60px,8vw,80px)',
                            background: T.softTeal, 
                            borderRadius: 8, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center' 
                          }}>
                            <RiInformationLine size="clamp(24px,3vw,32px)" style={{ color: T.accentGold }} />
                          </div>
                        )}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h2 className="t-venue-title" style={{
                          fontSize: 'clamp(18px,2.8vw,24px)',
                          fontWeight: 700,
                          color: T.text,
                          marginBottom: 8,
                          lineHeight: 1.3,
                        }}>
                          {pub.title || 'Untitled'}
                        </h2>
                        <div
                          dangerouslySetInnerHTML={{ __html: pub.description || pub.abstract || '' }}
                          style={{
                            fontSize: 'clamp(14px,1.2vw,16px)',
                            lineHeight: 1.8,
                            color: T.text,
                          }}
                        />
                        {pub.publicationDate && (
                          <div style={{ marginTop: 10, fontSize: 'clamp(12px,1vw,13px)', color: T.textMuted }}>
                            <RiCalendarLine style={{ display: 'inline', marginRight: 6 }} />
                            {new Date(pub.publicationDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                }

                return (
                  <motion.div
                    key={pub._id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ y: -4, boxShadow: T.shadowMd }}
                    className="t-publication-card"
                    style={{
                      background: T.white,
                      borderRadius: 'clamp(16px,2vw,20px)',
                      overflow: 'hidden',
                      boxShadow: T.shadow,
                      border: `1px solid ${T.border}`,
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'stretch',
                      minHeight: 'clamp(180px,25vw,220px)',
                    }}
                    onClick={() => openDetailsModal(pub)}
                  >
                    <div className="t-publication-cover" style={{
                      flex: '0 0 clamp(120px,18vw,180px)',
                      background: pub.coverImage ? `url(${pub.coverImage}) center/cover no-repeat` : T.gradSoft,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 'clamp(10px,1.5vw,16px)',
                      borderRight: `1px solid ${T.borderLight}`,
                      minHeight: 'clamp(160px,22vw,200px)',
                    }}>
                      {pub.coverImage ? (
                        <img
                          src={pub.coverImage}
                          alt={pub.title || 'Publication cover'}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            maxHeight: 'clamp(140px,20vw,180px)',
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<span style="font-size: clamp(32px,5vw,48px);">📄</span>';
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: 'clamp(32px,5vw,48px)', opacity: 0.5 }}>📄</span>
                      )}
                    </div>

                    <div className="t-publication-content" style={{
                      flex: 1,
                      padding: 'clamp(16px,2.5vw,20px) clamp(16px,2.5vw,24px)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      minWidth: 0,
                    }}>
                      <div>
                        {pub.category && (
                          <span style={{
                            display: 'inline-block',
                            padding: '2px 10px',
                            background: `${getCategoryColor(pub.category)}20`,
                            color: getCategoryColor(pub.category),
                            borderRadius: 20,
                            fontSize: 'clamp(9px,0.7vw,10px)',
                            fontWeight: 700,
                            marginBottom: 6,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}>
                            {getCategoryLabel(pub.category)}
                          </span>
                        )}
                        <h2 className="t-publication-title" style={{
                          fontSize: 'clamp(16px,2.5vw,20px)',
                          fontWeight: 700,
                          color: T.text,
                          marginBottom: 6,
                          lineHeight: 1.3,
                        }}>
                          {pub.title || 'Untitled'}
                        </h2>
                        
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          marginBottom: 6,
                          flexWrap: 'wrap',
                        }}>
                          <RiUserStarLine size="clamp(12px,1vw,14px)" style={{ color: T.textMuted }} />
                          <span style={{
                            fontSize: 'clamp(12px,1vw,13px)',
                            color: T.textMid,
                          }}>
                            {pub.authors && pub.authors.length > 0
                              ? pub.authors.map(a => a.name).join(', ')
                              : 'Anonymous'}
                          </span>
                        </div>

                        {pub.description && (
                          <div className="t-publication-desc" style={{
                            marginBottom: 10,
                            padding: 'clamp(6px,0.8vw,8px) clamp(10px,1.2vw,12px)',
                            background: T.offWhite,
                            borderRadius: 8,
                            border: `1px solid ${T.borderLight}`,
                          }}>
                            <p style={{
                              fontSize: 'clamp(12px,1vw,13px)',
                              color: T.textMid,
                              lineHeight: 1.6,
                              margin: 0,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}>
                              {pub.description}
                            </p>
                          </div>
                        )}

                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 'clamp(8px,1vw,12px) clamp(12px,1.5vw,20px)',
                          marginBottom: 6,
                        }}>
                          {pub.journalName && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <RiBookOpenLine size="clamp(12px,1vw,14px)" style={{ color: T.textMuted }} />
                              <span style={{ fontSize: 'clamp(11px,0.9vw,12px)', color: T.textMuted }}>
                                {pub.journalName}
                                {pub.volume && ` Vol ${pub.volume}`}
                                {pub.issue && `(${pub.issue})`}
                              </span>
                            </div>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <RiCalendarLine size="clamp(12px,1vw,14px)" style={{ color: T.textMuted }} />
                            <span style={{ fontSize: 'clamp(11px,0.9vw,12px)', color: T.textMuted }}>
                              {pub.publicationDate ? new Date(pub.publicationDate).getFullYear() : 'N/A'}
                            </span>
                          </div>
                          {pub.doi && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <RiLinksLine size="clamp(12px,1vw,14px)" style={{ color: T.textMuted }} />
                              <span style={{ fontSize: 'clamp(11px,0.9vw,12px)', color: T.primary }}>DOI: {pub.doi}</span>
                            </div>
                          )}
                        </div>

                        {pub.keywords && pub.keywords.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(4px,0.6vw,6px)', marginBottom: 6 }}>
                            {pub.keywords.slice(0, 3).map((keyword, idx) => (
                              <span key={idx} style={{
                                padding: '2px 8px',
                                background: T.offWhite,
                                borderRadius: 12,
                                fontSize: 'clamp(10px,0.8vw,11px)',
                                color: T.textMuted,
                                border: `1px solid ${T.border}`,
                              }}>
                                {keyword}
                              </span>
                            ))}
                            {pub.keywords.length > 3 && (
                              <span style={{ fontSize: 'clamp(10px,0.8vw,11px)', color: T.textMuted }}>
                                +{pub.keywords.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="t-publication-actions" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 'clamp(8px,1vw,12px)',
                        borderTop: `1px solid ${T.borderLight}`,
                        paddingTop: 'clamp(10px,1.2vw,14px)',
                        marginTop: 4,
                      }}>
                        <div style={{ display: 'flex', gap: 'clamp(12px,1.5vw,16px)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'clamp(11px,0.9vw,12px)', color: T.textMuted }}>
                            <RiEyeLine size="clamp(12px,1vw,14px)" /> {pub.views || 0}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'clamp(11px,0.9vw,12px)', color: T.textMuted }}>
                            <RiDownloadLine size="clamp(12px,1vw,14px)" /> {pub.downloads || 0}
                          </div>
                        </div>
                        <div className="t-action-buttons" style={{ display: 'flex', gap: 'clamp(6px,0.8vw,8px)', flexWrap: 'wrap' }}>
                          {pub.pdfUrl && (
                            <button
                              onClick={(e) => handleDownload(pub._id, e)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'clamp(4px,0.6vw,6px)',
                                padding: 'clamp(4px,0.6vw,6px) clamp(12px,1.5vw,16px)',
                                borderRadius: 30,
                                background: T.gradGold,
                                color: T.navyDeep,
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 'clamp(11px,0.9vw,12px)',
                                fontWeight: 600,
                                transition: 'all 0.2s',
                                boxShadow: T.shadowGold,
                                fontFamily: "'Inter', sans-serif"
                              }}
                              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                            >
                              <RiDownloadLine size="clamp(12px,1vw,14px)" /> PDF
                            </button>
                          )}
                          <button
                            onClick={() => openDetailsModal(pub)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'clamp(4px,0.6vw,6px)',
                              padding: 'clamp(4px,0.6vw,6px) clamp(12px,1.5vw,16px)',
                              borderRadius: 30,
                              background: T.white,
                              color: T.primary,
                              border: `1px solid ${T.primary}`,
                              cursor: 'pointer',
                              fontSize: 'clamp(11px,0.9vw,12px)',
                              fontWeight: 600,
                              transition: 'all 0.2s',
                              fontFamily: "'Inter', sans-serif"
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.background = T.primary;
                              e.currentTarget.style.color = T.white;
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background = T.white;
                              e.currentTarget.style.color = T.primary;
                            }}
                          >
                            Details
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Details Modal */}
        <AnimatePresence>
          {showModal && selectedPublication && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(8px)',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 'clamp(12px,3vw,20px)',
                overflowY: 'auto'
              }}
              onClick={closeModal}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.9, y: 20, opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                style={{
                  maxWidth: 900,
                  width: '100%',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  background: T.white,
                  borderRadius: 'clamp(20px,3vw,28px)',
                  boxShadow: T.shadowLg,
                  position: 'relative'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="t-modal-header" style={{
                  position: 'sticky',
                  top: 0,
                  background: T.gradPrimary,
                  padding: 'clamp(16px,2.5vw,24px) clamp(20px,3vw,32px)',
                  borderRadius: 'clamp(20px,3vw,28px) clamp(20px,3vw,28px) 0 0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  zIndex: 10,
                  gap: 'clamp(10px,1.5vw,16px)',
                  flexWrap: 'wrap'
                }}>
                  <div>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 10px',
                      background: 'rgba(255,255,255,0.2)',
                      color: T.white,
                      borderRadius: 20,
                      fontSize: 'clamp(10px,0.8vw,11px)',
                      fontWeight: 600,
                      marginBottom: 8,
                      textTransform: 'uppercase'
                    }}>
                      {getCategoryLabel(selectedPublication.category).toUpperCase()}
                    </span>
                    <h2 className="t-modal-title" style={{ 
                      fontSize: 'clamp(20px,2.8vw,24px)', 
                      fontWeight: 800, 
                      color: T.white, 
                      margin: 0, 
                      fontFamily: "'Playfair Display', serif" 
                    }}>
                      {selectedPublication.title}
                    </h2>
                  </div>
                  <button
                    onClick={closeModal}
                    className="t-modal-close"
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      border: 'none',
                      width: 'clamp(36px,4vw,40px)',
                      height: 'clamp(36px,4vw,40px)',
                      borderRadius: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: T.white,
                      transition: 'all 0.2s',
                      flexShrink: 0
                    }}
                  >
                    <RiCloseLine size="clamp(20px,2vw,24px)" />
                  </button>
                </div>
                
                <div className="t-modal-body" style={{ padding: 'clamp(20px,3vw,32px)' }}>
                  {selectedPublication.coverImage && (
                    <div style={{ 
                      marginBottom: 20, 
                      textAlign: 'center',
                      background: T.offWhite,
                      borderRadius: 12,
                      padding: 'clamp(12px,2vw,20px)'
                    }}>
                      <img 
                        src={selectedPublication.coverImage} 
                        alt={selectedPublication.title}
                        style={{
                          maxWidth: '100%',
                          maxHeight: 'clamp(120px,20vw,200px)',
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  <div style={{ marginBottom: 20 }}>
                    <h3 style={{ fontSize: 'clamp(14px,1.2vw,16px)', fontWeight: 700, color: T.text, marginBottom: 8 }}>Authors</h3>
                    <p style={{ fontSize: 'clamp(14px,1.2vw,15px)', color: T.textMid }}>
                      {selectedPublication.authors && selectedPublication.authors.length > 0
                        ? selectedPublication.authors.map(a => a.name).join(', ')
                        : 'Anonymous'}
                    </p>
                  </div>

                  {selectedPublication.description && (
                    <div style={{ marginBottom: 20 }}>
                      <h3 style={{ fontSize: 'clamp(14px,1.2vw,16px)', fontWeight: 700, color: T.text, marginBottom: 8 }}>Description</h3>
                      <p style={{ fontSize: 'clamp(14px,1.2vw,15px)', color: T.textMid, lineHeight: 1.7 }}>
                        {selectedPublication.description}
                      </p>
                    </div>
                  )}

                  {selectedPublication.abstract && (
                    <div style={{ marginBottom: 20 }}>
                      <h3 style={{ fontSize: 'clamp(14px,1.2vw,16px)', fontWeight: 700, color: T.text, marginBottom: 8 }}>Abstract</h3>
                      <div style={{ 
                        padding: 'clamp(12px,1.5vw,16px)',
                        background: T.offWhite,
                        borderRadius: 12,
                        border: `1px solid ${T.borderLight}`
                      }}>
                        <p style={{ fontSize: 'clamp(14px,1.2vw,15px)', color: T.textMid, lineHeight: 1.7 }}>
                          {selectedPublication.abstract}
                        </p>
                      </div>
                    </div>
                  )}

                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(150px,20vw,200px), 1fr))', 
                    gap: 'clamp(12px,1.5vw,16px)',
                    marginBottom: 20
                  }}>
                    {selectedPublication.journalName && (
                      <div>
                        <h4 style={{ fontSize: 'clamp(11px,0.9vw,12px)', color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Journal</h4>
                        <p style={{ fontSize: 'clamp(14px,1.2vw,15px)', fontWeight: 500, color: T.text }}>
                          {selectedPublication.journalName}
                          {selectedPublication.volume && ` Vol ${selectedPublication.volume}`}
                          {selectedPublication.issue && `(${selectedPublication.issue})`}
                        </p>
                      </div>
                    )}
                    {selectedPublication.publicationDate && (
                      <div>
                        <h4 style={{ fontSize: 'clamp(11px,0.9vw,12px)', color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Published</h4>
                        <p style={{ fontSize: 'clamp(14px,1.2vw,15px)', fontWeight: 500, color: T.text }}>
                          {formatDate(selectedPublication.publicationDate)}
                        </p>
                      </div>
                    )}
                    {selectedPublication.doi && (
                      <div>
                        <h4 style={{ fontSize: 'clamp(11px,0.9vw,12px)', color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>DOI</h4>
                        <p style={{ fontSize: 'clamp(13px,1.1vw,14px)', fontWeight: 500, color: T.primary }}>
                          {selectedPublication.doi}
                        </p>
                      </div>
                    )}
                  </div>

                  {selectedPublication.keywords && selectedPublication.keywords.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                      <h3 style={{ fontSize: 'clamp(14px,1.2vw,16px)', fontWeight: 700, color: T.text, marginBottom: 8 }}>Keywords</h3>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {selectedPublication.keywords.map((keyword, idx) => (
                          <span key={idx} style={{
                            padding: '4px 14px',
                            background: T.offWhite,
                            borderRadius: 20,
                            fontSize: 'clamp(12px,1vw,13px)',
                            color: T.textMid,
                            border: `1px solid ${T.border}`
                          }}>
                            {keyword}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ 
                    display: 'flex', 
                    gap: 'clamp(10px,1.5vw,16px)', 
                    flexWrap: 'wrap',
                    paddingTop: 'clamp(16px,2vw,20px)',
                    borderTop: `1px solid ${T.borderLight}`
                  }}>
                    {selectedPublication.pdfUrl && (
                      <button
                        onClick={(e) => handleDownload(selectedPublication._id, e)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: 'clamp(10px,1.2vw,12px) clamp(24px,3vw,32px)',
                          borderRadius: 40,
                          background: T.gradGold,
                          color: T.navyDeep,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 'clamp(14px,1.2vw,15px)',
                          fontWeight: 700,
                          transition: 'all 0.2s',
                          boxShadow: T.shadowGold,
                          fontFamily: "'Inter', sans-serif"
                        }}
                        onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                        onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        <RiDownloadLine size="clamp(18px,1.8vw,20px)" /> Download PDF
                      </button>
                    )}
                    <button
                      onClick={closeModal}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: 'clamp(10px,1.2vw,12px) clamp(24px,3vw,32px)',
                        borderRadius: 40,
                        background: T.white,
                        color: T.textMuted,
                        border: `1px solid ${T.border}`,
                        cursor: 'pointer',
                        fontSize: 'clamp(14px,1.2vw,15px)',
                        fontWeight: 600,
                        transition: 'all 0.2s',
                        fontFamily: "'Inter', sans-serif"
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = T.primary;
                        e.currentTarget.style.color = T.primary;
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = T.border;
                        e.currentTarget.style.color = T.textMuted;
                      }}
                    >
                      <RiCloseLine size="clamp(18px,1.8vw,20px)" /> Close
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* BACK TO TOP */}
        {showBtt && (
          <button
            className="t-back-to-top"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{
              position: 'fixed',
              bottom: 'clamp(16px,2.5vw,26px)',
              right: 'clamp(16px,2.5vw,26px)',
              width: 'clamp(36px,4vw,42px)',
              height: 'clamp(36px,4vw,42px)',
              borderRadius: 10,
              background: T.gradGold,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: T.navyDeep,
              boxShadow: T.shadowGold,
              zIndex: 100,
            }}
          >
            <RiArrowUpLine size="clamp(16px,1.5vw,18px)" />
          </button>
        )}
      </div>
    </>
  );
}